// Клиент `/api/v1/me/sessions*` и `PATCH /api/v1/me/preferences`.
//
// Управление серверными сессиями (ADR-0012): список активных, отзыв
// конкретной / всех кроме текущей, переключение remember-me. JWT
// прикручивает `authedFetch`. Типы вшиты руками параллельно с backend
// Pydantic-схемами — тот же паттерн, что в `features/trustRules/api.ts`.

import { authedFetch } from "../../auth/authedFetch";
import type { MeResponse } from "../auth/api";

export interface ActiveSessionResponse {
  readonly id: string;
  readonly last_seen_at: string;
  readonly absolute_expires_at: string;
  readonly remember: boolean;
  readonly ip: string | null;
  readonly user_agent: string | null;
  readonly current: boolean;
}

export interface RevokeOthersResponse {
  readonly revoked: number;
}

/**
 * HTTP-ошибка с числовым `status`. Зеркало `trustRules.HttpError`.
 */
export class HttpError extends Error {
  public readonly status: number;
  public constructor(status: number, message: string) {
    super(message);
    this.name = "HttpError";
    this.status = status;
  }
}

function buildHeaders(): Record<string, string> {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

export async function fetchSessions(): Promise<ActiveSessionResponse[]> {
  const response = await authedFetch("/api/v1/me/sessions", {
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `fetchSessions: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as ActiveSessionResponse[];
}

export async function revokeSession(sessionId: string): Promise<void> {
  const response = await authedFetch(`/api/v1/me/sessions/${sessionId}`, {
    method: "DELETE",
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `revokeSession: HTTP ${String(response.status)}`,
    );
  }
}

export async function revokeOtherSessions(): Promise<RevokeOthersResponse> {
  const response = await authedFetch("/api/v1/me/sessions/revoke-others", {
    method: "POST",
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `revokeOtherSessions: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as RevokeOthersResponse;
}

export async function updateRememberSessions(
  rememberSessions: boolean,
): Promise<MeResponse> {
  const response = await authedFetch("/api/v1/me/preferences", {
    method: "PATCH",
    headers: buildHeaders(),
    body: JSON.stringify({ remember_sessions: rememberSessions }),
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `updateRememberSessions: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as MeResponse;
}
